import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Button, Card, Column, Divider, Row, Typography } from 'seagull';
import { ArrowRight, Bulb } from 'seagull/icons';
import RecipientDetails from '~/components/RecipientDetails/RecipientDetails';
import { relationshipModel } from '~/models';
import { fromDateStampObject } from '~/models/time';
import { formatLongMonthUSDate } from '~/utils';
import { getLimitedTimeDiscountOfferCalloutText } from '../../WelcomeStep/welcomeStepUtils';
import { GetCoverPageTID } from './CoverPage';
import styles from './CoverPageDesktop.module.scss';
import { CoverPageHeader } from './CoverPageHeader/CoverPageHeader';
export const CoverPageDesktop = (param)=>{
    let { testId, relationship, onGetStarted, isPreview } = param;
    var _relationship_signatories;
    const { CoverPageTestIds } = useMemo(()=>GetCoverPageTID(testId), [
        testId
    ]);
    const vendorName = relationship.vendor.name;
    const clientName = relationship.customer.name;
    const vendorLogoUrl = relationshipModel.vendorLogoUrl({
        vendor: relationship.vendor
    });
    const expirationDate = relationship.expirationDate ? fromDateStampObject(relationship.expirationDate) : null;
    const discountCalloutText = getLimitedTimeDiscountOfferCalloutText(relationship);
    const hasExpirationInfo = expirationDate || discountCalloutText;
    return /*#__PURE__*/ _jsx(Card, {
        padding: "m",
        className: styles.card,
        testId: CoverPageTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Column, {
            className: styles.container,
            children: [
                !isPreview && /*#__PURE__*/ _jsx(CoverPageHeader, {
                    testId: testId,
                    relationship: relationship
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    className: styles.content,
                    gap: 32,
                    children: [
                        vendorLogoUrl && /*#__PURE__*/ _jsx("img", {
                            "data-testid": CoverPageTestIds.VendorLogo(),
                            src: vendorLogoUrl,
                            alt: vendorName,
                            className: styles.vendorLogo
                        }),
                        /*#__PURE__*/ _jsxs(Column, {
                            children: [
                                /*#__PURE__*/ _jsxs(Column, {
                                    gap: 4,
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            testId: CoverPageTestIds.ProposalTitle(),
                                            variant: "h1",
                                            weight: "bold",
                                            fontFamily: "hero",
                                            children: "Proposal for review"
                                        }),
                                        /*#__PURE__*/ _jsxs(Typography, {
                                            testId: CoverPageTestIds.ProposalPreparedFor(),
                                            variant: "h4",
                                            weight: "normal",
                                            fontFamily: "hero",
                                            children: [
                                                "Prepared for ",
                                                clientName
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(Typography, {
                                            testId: CoverPageTestIds.PropsalDescription(),
                                            children: [
                                                vendorName,
                                                " has sent you this proposal and is requesting that you sign and accept it."
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Divider, {
                                    margin: 16
                                }),
                                hasExpirationInfo && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs(Row, {
                                            gap: 8,
                                            alignItems: "start",
                                            children: [
                                                /*#__PURE__*/ _jsx(Box, {
                                                    className: styles.primaryIcon,
                                                    children: /*#__PURE__*/ _jsx(Bulb, {})
                                                }),
                                                /*#__PURE__*/ _jsxs(Column, {
                                                    gap: 4,
                                                    children: [
                                                        expirationDate && /*#__PURE__*/ _jsxs(Typography, {
                                                            testId: CoverPageTestIds.ProposalExpirationDate(),
                                                            children: [
                                                                "Please note that this proposal is valid until",
                                                                ' ',
                                                                formatLongMonthUSDate(expirationDate),
                                                                "."
                                                            ]
                                                        }),
                                                        discountCalloutText && /*#__PURE__*/ _jsx(Typography, {
                                                            testId: CoverPageTestIds.ProposalDiscountCallout(),
                                                            children: discountCalloutText
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(Divider, {
                                            margin: 16
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Column, {
                                    gap: 8,
                                    className: styles.recipientsSection,
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            testId: CoverPageTestIds.RecipientsTitle(),
                                            variant: "paragraph2",
                                            skin: "secondary",
                                            children: "Recipients"
                                        }),
                                        /*#__PURE__*/ _jsx(RecipientDetails, {
                                            className: styles.recipient,
                                            firstName: relationship.buyer.firstName,
                                            lastName: relationship.buyer.lastName,
                                            badge: "required",
                                            requiredBadgeSkin: "primary",
                                            requiredBadgeClassName: styles.requiredBadge
                                        }),
                                        (_relationship_signatories = relationship.signatories) === null || _relationship_signatories === void 0 ? void 0 : _relationship_signatories.map((signatory)=>/*#__PURE__*/ _jsx(RecipientDetails, {
                                                testId: CoverPageTestIds.Recipient(signatory.id),
                                                className: styles.recipient,
                                                firstName: signatory.firstName,
                                                lastName: signatory.lastName,
                                                badge: relationshipModel.hasSignatorySigned(relationship, signatory.id) ? 'signed' : 'required',
                                                requiredBadgeSkin: "primary",
                                                requiredBadgeClassName: styles.requiredBadge
                                            }, signatory.id)),
                                        relationship.reviewers.map((reviewer)=>/*#__PURE__*/ _jsx(RecipientDetails, {
                                                testId: CoverPageTestIds.Reviewer(reviewer.id),
                                                className: styles.recipient,
                                                firstName: reviewer.firstName,
                                                lastName: reviewer.lastName
                                            }, reviewer.id))
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Row, {
                            children: /*#__PURE__*/ _jsx(Button, {
                                testId: CoverPageTestIds.GetStarted(),
                                suffixIcon: /*#__PURE__*/ _jsx(ArrowRight, {
                                    size: 16
                                }),
                                onClick: onGetStarted,
                                className: styles.button,
                                children: "Get started"
                            })
                        })
                    ]
                })
            ]
        })
    });
};
